/* =============================================================================
   Greffe de la mesure d'écart pupillaire dans le tunnel « Personnaliser mes
   verres ».

   Ce fichier ne modifie pas lens-tunnel.js. Le tunnel reconstruit son contenu
   (#lt-stage) à chaque changement d'étape, donc on l'observe et on réinjecte le
   bouton dès que le bloc d'écart pupillaire réapparaît. Les seuls points de
   contact sont trois sélecteurs :

     input[data-ecart="od"]   demi-écart œil droit
     input[data-ecart="og"]   demi-écart œil gauche
     #lt-ecart-total          écart total

   Si le tunnel change de structure, la greffe cesse simplement d'apparaître :
   elle ne casse rien.
   ============================================================================= */

/* Le moteur est charge par l'URL que Liquid nous donne, pas par un chemin
   relatif. Shopify sert ses assets avec un parametre de version (?v=...) qui
   change a chaque modification ; un import relatif le supprime, et le
   navigateur comme le CDN resservent alors indefiniment l'ancienne copie. Un
   correctif deploye restait invisible. Au passage, le moteur n'est telecharge
   qu'au clic sur le bouton. */
let moteur = null;
// Le module une fois résolu, pour pouvoir relire ses seuils EFFECTIFS. Les
// annoncer dans le journal évite la question qui revient sans cesse : la
// version en ligne est-elle bien celle qu'on croit, et les réglages passés en
// URL ont-ils pris ? Un en-tête qui le dit vaut mieux qu'une supposition.
let moteurPret = null;
function chargerMoteur() {
  if (!moteur) {
    moteur = import(/* webpackIgnore: true */ CFG.moteur || './ecart-pupillaire.js');
    moteur.then((m) => { moteurPret = m; }).catch(() => {});
  }
  return moteur;
}

const CFG = window.LensTunnelEP || {};
const MARQUE = 'data-ep-greffe';

const CSS = `
.ep-tunnel-overlay{
  position:fixed; inset:0; z-index:2147483000;
  display:flex; align-items:center; justify-content:center;
  padding:16px; background:rgba(12,12,12,.82);
  -webkit-backdrop-filter:blur(4px); backdrop-filter:blur(4px);
  overflow-y:auto; -webkit-overflow-scrolling:touch;
}
.ep-tunnel-box{ position:relative; width:100%; max-width:560px; margin:auto; }
.ep-tunnel-close{
  position:absolute; top:-6px; right:-2px; transform:translateY(-100%);
  width:40px; height:40px; padding:0; border:0; border-radius:50%;
  background:rgba(255,255,255,.14); color:#fff; font-size:26px; line-height:1;
  cursor:pointer;
}
.ep-tunnel-btn{
  display:flex; align-items:center; justify-content:center; gap:9px;
  width:100%; margin-top:12px; padding:13px 18px;
  border:1px solid var(--lt-accent-dk,#025250); border-radius:6px;
  background:var(--lt-accent-soft,#e3efee); color:var(--lt-accent-dk,#025250);
  font:inherit; font-size:14.5px; font-weight:600; cursor:pointer;
}
.ep-tunnel-btn:hover{ background:#fff; }
.ep-tunnel-btn svg{ width:26px; height:15px; fill:none; stroke:currentColor; stroke-width:1.6; }
.ep-tunnel-note{ font-size:12px; line-height:1.55; color:var(--lt-muted,#6b6b6b); margin:8px 0 0; }
.ep-tunnel-ok{
  margin:10px 0 0; padding:10px 12px; border-radius:6px;
  background:rgba(47,191,113,.12); color:#1d7a47;
  font-size:12.5px; line-height:1.55;
}
.ep-tunnel-ok.warn{ background:rgba(232,163,61,.14); color:#8a5a13; }
.ep-tunnel-reprise{
  max-width:560px; margin:0 auto 14px; padding:15px 18px; border-radius:10px;
  background:#fff; color:#1c1c1c; font-size:15px; line-height:1.55;
}
.ep-journal-actions{ display:flex; gap:8px; margin-top:10px; flex-wrap:wrap; }
.ep-journal-actions button{
  flex:1 1 auto; min-height:44px; padding:0 14px; border-radius:8px;
  border:1px solid var(--lt-line-strong,#d3d3cf); background:#f4f5f4;
  font:inherit; font-size:14px; font-weight:600; color:#1c1c1c; cursor:pointer;
}
.ep-journal-actions button.primaire{ background:#025250; border-color:#025250; color:#fff; }
`;

const LUNETTES =
  '<svg viewBox="0 0 26 17" aria-hidden="true">' +
  '<circle cx="7" cy="9" r="5.5"/><circle cx="19" cy="9" r="5.5"/>' +
  '<path d="M12.5 9h1M1 9 .2 7.5M25 9l.8-1.5"/></svg>';

let styleFait = false;
function poserStyle() {
  if (styleFait) return;
  styleFait = true;
  const el = document.createElement('style');
  el.textContent = CSS;
  document.head.append(el);
}

// L'usage optique arrondit les demi-écarts au demi-millimètre.
const auDemi = (v) => Math.round(v * 2) / 2;
const virgule = (v) => v.toFixed(1).replace('.', ',');

/* Le tunnel lit ses champs par un gestionnaire oninput : écrire la valeur ne
   suffit pas, il faut déclencher l'événement pour que son état interne suive,
   sinon le bouton « Continuer » resterait désactivé. */
function remplir(champ, valeur) {
  if (!champ) return;
  champ.value = valeur;
  champ.dispatchEvent(new Event('input', { bubbles: true }));
}

/* Un client qui mesure ici le fait parce qu'il ne connaît PAS son écart
   pupillaire. Lui répondre « refaites, ou saisissez vos valeurs à la main » est
   une impasse : il n'a rien à saisir. Toute issue doit donc rester praticable.
   D'où : on réessaie une fois en disant quoi corriger, puis on accepte la
   mesure en annonçant la vérification par l'opticien, qui a lieu de toute
   façon avant fabrication. On ne renonce que sur une valeur physiquement
   impossible, et là on oriente vers la boutique. */
const EP_MIN = 48;   // mm, en deçà d'un écart pupillaire adulte plausible
const EP_MAX = 78;
const ASYM_MAX = 3;  // mm d'écart entre les deux demi-écarts

function qualite(r) {
  if (!(r.pdFar >= EP_MIN && r.pdFar <= EP_MAX)) return 'aberrante';
  if (Math.abs(r.odFar - r.ogFar) > ASYM_MAX) return 'asymetrique';
  if (r.confidence === 'low') return 'instable';
  return 'bonne';
}

const CONSEIL = {
  instable: 'Les deux mesures ne concordent pas. Suivez bien les consignes de distance, bougez le moins possible pendant l’acquisition, et placez-vous face à une fenêtre ou une lampe plutôt que dos à elle.',
  asymetrique: 'Gardez la tête bien droite et face à l’objectif, sans la tourner d’un côté, et tenez l’appareil à hauteur des yeux.',
  aberrante: 'Retirez vos lunettes, tenez l’appareil à bout de bras à hauteur des yeux, et vérifiez que votre visage est bien dans le repère.',
};

function message(bloc, texte, alerte) {
  let note = bloc.querySelector('.ep-tunnel-ok');
  if (!note) {
    note = document.createElement('p');
    note.className = 'ep-tunnel-ok';
    bloc.append(note);
  }
  note.className = 'ep-tunnel-ok' + (alerte ? ' warn' : '');
  note.textContent = texte;
}

function appliquer(bloc, r, sur) {
  const od = auDemi(r.odFar);
  const og = auDemi(r.ogFar);
  remplir(bloc.querySelector('input[data-ecart="od"]'), virgule(od));
  remplir(bloc.querySelector('input[data-ecart="og"]'), virgule(og));
  // Le total doit être cohérent avec les deux demi-écarts affichés, sinon le
  // client voit trois nombres qui ne s'additionnent pas.
  remplir(document.getElementById('lt-ecart-total'), virgule(od + og));

  const val = `${virgule(od)} et ${virgule(og)} mm, soit ${virgule(od + og)} mm au total`;
  message(bloc, sur
    // Pas de promesse de vérification : sur une commande à distance, personne ne
    // reverra le client avant la fabrication. On avertit, on n'engage pas.
    ? `Mesuré avec votre caméra : ${val}. Mesure indicative : rien ne remplace une prise de mesure par un opticien.`
    : `Mesure enregistrée : ${val}. Elle a été un peu difficile à stabiliser : refaites-la avec le bouton ci-dessus si vous voulez, sinon nos opticiens la vérifieront avec vous avant de fabriquer vos verres.`,
    !sur);
}

function ouvrir(bloc) {
  poserStyle();
  let essais = 0;
  const maxEssais = Number(CFG.essais) || 1;   // une reprise avant d'accepter

  const ov = document.createElement('div');
  ov.className = 'ep-tunnel-overlay';
  ov.innerHTML =
    '<div class="ep-tunnel-box">' +
    '<button type="button" class="ep-tunnel-close" aria-label="Fermer la mesure">×</button>' +
    '<div class="ep-tunnel-reprise" hidden></div>' +
    '<div data-ep-root' +
    ` data-ep-iris="${CFG.iris || 12.8}"` +
    ` data-ep-passes="${CFG.passes || 3}"` +
    ` data-ep-fov="${CFG.fov || 63}"` +
    ` data-ep-midline="${CFG.midline || 0}"` +
    ' data-ep-show-near="false"></div></div>';
  document.body.append(ov);

  const reprise = ov.querySelector('.ep-tunnel-reprise');
  const widget = ov.querySelector('[data-ep-root]');

  // En mode diagnostic (?diag=1), le panneau reste ouvert après une mesure
  // réussie : le bloc de diagnostic est affiché sur l'écran de résultat, et le
  // refermer aussitôt le rendrait illisible. Les champs sont remplis comme
  // d'habitude, seule la fermeture est suspendue, et la croix reste active.
  const DIAG = (() => {
    try { return new URLSearchParams(location.search).has('diag'); } catch (_) { return false; }
  })();

  const fermer = () => {
    // Coupe la caméra même si la mesure est en cours.
    ov.querySelectorAll('video').forEach((v) => {
      const f = v.srcObject;
      if (f && f.getTracks) f.getTracks().forEach((t) => t.stop());
    });
    ov.remove();
    document.removeEventListener('ep:result', surResultat);
  };

  /* --- Journal des mesures, en diagnostic seulement ------------------------
     Relever un chiffre à l'écran suppose de faire une capture juste après la
     mesure, donc de lâcher la posture au pire moment. Chaque résultat est donc
     consigné, et un bouton copie l'ensemble d'un coup. Le stockage est local au
     navigateur, il survit aux rechargements et ne part nulle part. */
  const CLE = 'ep-journal-diag';

  const lireJournal = () => {
    try { return JSON.parse(localStorage.getItem(CLE)) || []; } catch (_) { return []; }
  };
  const ecrireJournal = (j) => {
    try { localStorage.setItem(CLE, JSON.stringify(j)); } catch (_) { /* mode privé */ }
  };

  const consigner = (r) => {
    const j = lireJournal();
    j.push({
      h: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      pd: r.pdFar, od: r.odFar, og: r.ogFar,
      lacet: r.yawDeg, median: r.midlineMm, iris: r.irisPx, dist: r.distanceMm,
      passes: r.distances || null,
      disp: r.spread, n: r.samples, conf: r.confidence,
    });
    ecrireJournal(j);
    return j;
  };

  // Les seuils tels que le moteur les applique réellement, pas ceux qu'on
  // suppose. Si une version périmée est en cache, la ligne le montre.
  const reglagesEffectifs = () => {
    const T = moteurPret && moteurPret.TH;
    if (!T) return 'moteur non chargé';
    const d = T.deplacementEntrePasses;
    return `lacet max ${T.yawDeg}°, plan médian ${T.midlineParDegreLacet} mm/°/œil, `
      + `déplacement entre passes ${d === 'auto' ? 'auto' : (d ? 'forcé actif' : 'désactivé')}`;
  };

  // Un tableau en texte simple : lisible tel quel dans un message, et les
  // colonnes restent alignées même sans mise en forme.
  const formaterJournal = (j) => {
    const nb = (v, d = 1) => (v == null || isNaN(v) ? '—' : v.toFixed(d));
    const l = [
      `Journal de mesure, ${j.length} essai(s), iris ${CFG.iris} mm, median ${CFG.midline || 0} mm`,
      reglagesEffectifs(),
      'heure  total    OD     OG    asym   lacet   median  iris px  dist  disp  passes',
    ];
    for (const m of j) {
      l.push(
        `${m.h}  ${nb(m.pd)}  ${nb(m.od)}  ${nb(m.og)}  ` +
        `${m.od - m.og >= 0 ? '+' : ''}${nb(m.od - m.og, 2)}  ` +
        `${m.lacet >= 0 ? '+' : ''}${nb(m.lacet)}°  ` +
        `${m.median >= 0 ? '+' : ''}${nb(m.median, 2)}  ` +
        `${nb(m.iris)}  ${nb(m.dist, 0)}  ${nb(m.disp, 2)}` +
        (m.passes ? `  [${m.passes.join(' ')}]` : '')
      );
    }
    const lacets = j.map((m) => m.lacet).filter((v) => !isNaN(v));
    if (lacets.length > 1) {
      const moy = lacets.reduce((a, b) => a + b, 0) / lacets.length;
      const memeSigne = lacets.every((v) => v >= 0) || lacets.every((v) => v <= 0);
      l.push(`lacet moyen ${moy >= 0 ? '+' : ''}${moy.toFixed(1)}°, ` +
             (memeSigne ? 'toujours du même côté' : 'change de côté'));
    }
    return l.join('\n');
  };

  // Fermeture normale, sauf en diagnostic où l'on garde l'écran de résultat
  // sous les yeux. Le message dit pourquoi le panneau ne s'est pas refermé,
  // sans quoi cela passerait pour un blocage.
  const terminer = (r) => {
    if (!DIAG) { fermer(); return; }
    const j = r ? consigner(r) : lireJournal();
    reprise.hidden = false;
    reprise.innerHTML =
      `<strong>Mesure ${j.length} enregistrée.</strong> Les champs sont remplis. ` +
      'Refaites un essai avec le bouton, ou copiez tout quand vous avez fini.' +
      '<div class="ep-journal-actions">' +
        `<button type="button" class="primaire" data-journal-copier>Copier les ${j.length} mesures</button>` +
        '<button type="button" data-journal-vider>Vider</button>' +
      '</div>';

    reprise.querySelector('[data-journal-copier]').addEventListener('click', async (ev) => {
      const b = ev.currentTarget;
      try {
        await navigator.clipboard.writeText(formaterJournal(lireJournal()));
        b.textContent = 'Copié';
      } catch (_) {
        // Safari refuse parfois le presse-papier : on montre le texte, il reste
        // sélectionnable à la main plutôt que perdu.
        b.textContent = 'Copie refusée, texte ci-dessous';
        const pre = document.createElement('pre');
        pre.style.cssText = 'white-space:pre-wrap;font-size:12px;user-select:text;margin:10px 0 0';
        pre.textContent = formaterJournal(lireJournal());
        reprise.append(pre);
      }
    });
    reprise.querySelector('[data-journal-vider]').addEventListener('click', () => {
      ecrireJournal([]);
      reprise.innerHTML = '<strong>Journal vidé.</strong> Fermez avec la croix.';
    });
  };

  function surResultat(e) {
    const r = e.detail;
    const q = qualite(r);

    if (q === 'bonne') { appliquer(bloc, r, true); terminer(r); return; }

    if (essais < maxEssais) {
      essais += 1;
      // On masque le widget le temps du message : sinon le client aperçoit une
      // fraction de seconde les chiffres qu'on s'apprête justement à écarter.
      widget.style.visibility = 'hidden';
      reprise.hidden = false;
      reprise.innerHTML = '<strong>On recommence.</strong> ' + CONSEIL[q];
      setTimeout(() => {
        const refaire = widget.querySelector('[data-ep-retry]');
        if (!refaire) { appliquer(bloc, r, false); terminer(r); return; }
        reprise.hidden = true;
        widget.style.visibility = '';
        refaire.click();
      }, 2600);
      return;
    }

    if (q === 'aberrante') {
      // Deux mesures physiquement impossibles : insister ne mènerait nulle
      // part, mais il reste une issue concrète.
      message(bloc, CFG.aide || 'La mesure n’a pas abouti sur cet appareil. Réessayez avec le bouton ci-dessus, en pleine lumière, sans lunettes et le visage bien dans le repère. Si cela ne passe toujours pas, passez en boutique à Craponne ou écrivez-nous : nous mesurons votre écart en deux minutes.', true);
      terminer();
      return;
    }

    appliquer(bloc, r, false);
    terminer(r);
  }

  document.addEventListener('ep:result', surResultat);
  ov.querySelector('.ep-tunnel-close').addEventListener('click', fermer);
  ov.addEventListener('click', (e) => { if (e.target === ov) fermer(); });

  chargerMoteur().then((m) => m.init(ov)).catch((err) => {
    console.warn('[EP] moteur de mesure indisponible', err);
    ov.querySelector('.ep-tunnel-reprise').hidden = false;
    ov.querySelector('.ep-tunnel-reprise').textContent =
      'La mesure n’a pas pu se charger. Vérifiez votre connexion et réessayez.';
  });
}

function greffer() {
  const champOD = document.querySelector('input[data-ecart="od"]');
  if (!champOD) return;
  const bloc = champOD.closest('.lt-fieldset');
  if (!bloc || bloc.hasAttribute(MARQUE)) return;
  bloc.setAttribute(MARQUE, '');

  // Les liens vers l'application tierce n'ont plus lieu d'être.
  bloc.querySelectorAll('p').forEach((p) => {
    if (/PDCheck/i.test(p.textContent)) p.remove();
  });

  poserStyle();
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ep-tunnel-btn';
  btn.innerHTML = LUNETTES + (CFG.label || 'Mesurer avec ma caméra');
  btn.addEventListener('click', () => ouvrir(bloc));
  bloc.append(btn);

  const note = document.createElement('p');
  note.className = 'ep-tunnel-note';
  note.textContent = CFG.note
    || 'Vous ne connaissez pas votre écart pupillaire ? Mesurez-le en quelques secondes, sans rien installer. Tout se passe dans votre navigateur : aucune image n’est envoyée ni conservée.';
  btn.after(note);
}

/* Le tunnel remplace le contenu de #lt-stage à chaque étape : un simple appel
   au chargement ne suffirait pas. On observe, en regroupant les rafales de
   mutations sur une seule image d'animation. */
let planifie = false;
function planifier() {
  if (planifie) return;
  planifie = true;
  requestAnimationFrame(() => { planifie = false; greffer(); });
}

if (typeof document !== 'undefined') {
  new MutationObserver(planifier).observe(document.documentElement, {
    childList: true, subtree: true,
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', greffer);
  } else {
    greffer();
  }
}
